import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Box, Tooltip } from "@mui/material";
import styles from "@/components/anchorDataOverview/anchorDataOverview.module.css";
import EditIcon from "@mui/icons-material/Edit";
import GenericButton from "@/components/forms/inputs/button/genericButton";

type AnchorDataOverviewObjectListCellEditorProps = {
  row: any;
  columnId: string;
  headerLabel: string;
  formatObjectListEntries: (
    value: any[],
    fieldOrder?: string[],
    t?: any,
  ) => React.ReactNode;
  objectListFieldOrder: string[] | undefined;
  onEditClick: (row: any) => void;
};

const AnchorDataOverviewObjectListCellEditor: FC<
  AnchorDataOverviewObjectListCellEditorProps
> = ({
  row,
  columnId,
  headerLabel,
  formatObjectListEntries,
  objectListFieldOrder,
  onEditClick,
}) => {
  const { t } = useTranslation();
  const templateId = columnId.replace(/^value_/, "");
  const isInherited = !!row.original[`is_inherited_${templateId}`];
  const inheritedFrom = row.original[`inherited_from_${templateId}`];
  const value = row.original[columnId];
  let displayContent: React.ReactNode = "-";
  if (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === "object" &&
    value[0] !== null
  ) {
    displayContent = formatObjectListEntries(value, objectListFieldOrder, t);
  } else if (
    typeof value === "string" &&
    value.trim().startsWith("[") &&
    value.trim().endsWith("]")
  ) {
    try {
      const arr = JSON.parse(value.replace(/'/g, '"'));
      if (Array.isArray(arr)) {
        if (arr.length > 0 && typeof arr[0] === "object" && arr[0] !== null) {
          displayContent = formatObjectListEntries(
            arr,
            objectListFieldOrder,
            t,
          );
        } else {
          displayContent = arr.join(", ");
        }
      }
    } catch {
      displayContent = value;
    }
  } else if (Array.isArray(value)) {
    if (value.length > 0 && typeof value[0] === "object" && value[0] !== null) {
      displayContent = formatObjectListEntries(value, objectListFieldOrder, t);
    } else {
      displayContent = value.join(", ");
    }
  } else if (value != null && value !== "") {
    displayContent = String(value);
  }
  const contentArea = isInherited ? (
    <Box component="span" className={styles.inheritedValueWrapper}>
      <Tooltip
        placement="top"
        arrow
        title={
          <div className={styles.tooltipContent}>
            <span>{headerLabel}</span>
            <br />
            <br />
            <span className={styles.labelinheritedInfo}>
              {t("inherited_value")}
            </span>
            <br />
            <span className={styles.labelinheritedInfo}>
              {t("inherited_from")}:{" "}
            </span>
            {inheritedFrom?.name ?? "-"}
          </div>
        }
      >
        <span className={styles.inheritedIconTable}>ⓘ</span>
      </Tooltip>
      <span className={styles.inheritedValueText}>{displayContent}</span>
    </Box>
  ) : (
    displayContent
  );
  return (
    <Box className={styles.objectListCellEditorContainer}>
      <Tooltip title={t("edit")}>
        <GenericButton
          variant="icon"
          color="iconBase"
          size="small"
          onClick={() => onEditClick(row)}
          aria-label={t("edit")}
        >
          <EditIcon fontSize="small" />
        </GenericButton>
      </Tooltip>
      <Box className={styles.objectListCellEditorContent}>{contentArea}</Box>
    </Box>
  );
};

export default AnchorDataOverviewObjectListCellEditor;
